import { ElementType } from 'react';
import { CtaList } from '@/app/_components/core/cta-list';
import { EditableMarkup, EditableText } from '@/app/_components/core/editable-markup';
import { Headline } from '@/app/_components/core/headline';
import RespImage from '@/app/_components/core/resp-image';
import { RichText } from "@/app/_components/core/rich-text";
import { Tagline } from '@/app/_components/core/tagline';
import { TwoColumnLayout } from '@/app/_components/core/two-column-layout';
import type { HeroItem } from '@/app/_components/shared/hero/hero.types';
import { transformSrcsetCandidates } from '@/app/_lib/image-art-direction';
import { emptyToUndefined } from '@/app/_lib/utils';

// Image sizes for 1:1 square image in TwoColumnLayout
// Mobile calc: viewport - container padding (16px * 2)
const IMAGE_SIZES =
  '(min-width: 1920px) 712px, (min-width: 1280px) 584px, (min-width: 1024px) 456px, (min-width: 768px) 720px, (min-width: 640px) 592px, calc(100vw - 48px)';

interface HeroItemContentProps {
  item: HeroItem;
  priority?: boolean;
  /** Tracking namespace for the CTAs (e.g. "hero" or "hero-slide-2"). */
  trackingPrefix?: string;
}

export const HeroItemContent = ({ item, priority, trackingPrefix }: HeroItemContentProps) => {
  const { headline, headlineType, subheadline, bodytext, ctas, image, mediaPosition, inlineEditing } = item;
  // In the visual editor the CMS's markup replaces a field, and an empty
  // field still renders, so it can be filled.
  const hasSubheadline = [subheadline, inlineEditing?.subheadline].some(Boolean);
  const hasHeadline = [headline, inlineEditing?.headline].some(Boolean);

  const { srcsetCandidates: transformedSrcset, artDirection } = image
    ? transformSrcsetCandidates(image.srcsetCandidates)
    : { srcsetCandidates: [], artDirection: undefined };

  return (
    <TwoColumnLayout
      position={mediaPosition}
      className="h-full items-center gap-8 lg:gap-16"
    >
      <TwoColumnLayout.Left className="aspect-square w-full overflow-hidden rounded-hero-media-radius">
        {image && (
          <RespImage
            srcsetCandidates={transformedSrcset}
            artDirection={artDirection}
            alt={image.altText ?? ''}
            title={emptyToUndefined(image.title)}
            sizes={IMAGE_SIZES}
            className="size-full  object-cover"
            priority={priority}
            editorAttributes={image.editorAttributes}
          />
        )}
      </TwoColumnLayout.Left>

      <TwoColumnLayout.Right className="flex flex-col justify-center gap-6">
        {hasSubheadline && (
          <Tagline align="left" className="mb-0">
            <EditableText markup={inlineEditing?.subheadline}>{subheadline}</EditableText>
          </Tagline>
        )}

        {hasHeadline && (
          <Headline as={(emptyToUndefined(headlineType) ?? 'h1') as ElementType} variant="h1" className="mb-0">
            <EditableText markup={inlineEditing?.headline}>{headline}</EditableText>
          </Headline>
        )}

        {inlineEditing?.bodytext ? (
          <EditableMarkup html={inlineEditing.bodytext} className="max-w-2xl editable-rich-text" />
        ) : (
          bodytext && <RichText>{bodytext}</RichText>
        )}

        <CtaList ctas={ctas} trackingPrefix={trackingPrefix} />
      </TwoColumnLayout.Right>
    </TwoColumnLayout>
  );
};
